import React, { useState, useRef, useEffect, useCallback } from "react";
import OpenAI from "openai";
import {
  
  
  Users,
  
  Phone,
  PhoneOff,
  Image,
  Send,
  Mic,
  MicOff,
  Clock,
  
  Globe,
  
} from "lucide-react";
import DemoSidebar from './DemoSidebar';
import { SCHEDULE_DATA } from './scheduleData';
import aiImage from "../images/cortana.jpg";
import { ElevenLabsService } from "./ElevenLabsService";

// API Keys
const ELEVEN_LABS_API_KEY =
  "sk_6effc1af26155b8db10f4ab1aa69d005bb747746c9821ffe";
const VOICE_ID = "21m00Tcm4TlvDq8ikWAM";

// OpenAI configuration
const openai = new OpenAI({
  apiKey:
    "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",
  dangerouslyAllowBrowser: true,
});

// Quick templates that appear above chat input
const QUICK_TEMPLATES = [
    
    
    
    // Original templates with categories
    {
      id: "urgent-tasks",
      label: "Today's Tasks",
      prompt: "What are the important tasks I need to complete for my upcoming Calculus class on Chain Rule? Include preparation checklist and timeline.",
      category: "tasks"
    },
    // {
    //   id: "lesson-plan",
    //   label: "Create Lesson Plan",
    //   prompt: "Create a detailed lesson plan for Mathematics focusing on Calculus derivatives.",
    //   category: "content"
    // },
    
    {
      id: "worksheet",
      label: "Generate Worksheet",
      prompt: "Create an intermediate worksheet for Chain Rule with 10 practice problems.",
      category: "content"
    },
    {
      id: "quiz",
      label: "Create Quiz",
      prompt: "Generate a 5-minute quiz on Chain Rule with 5 questions. Include answer key.",
      category: "assessment"
    },
    {
      id: "check-availability",
      label: "Check Availability",
      prompt: "Can you check my schedule this week and tell me when I have free time?",
      category: "schedule"
    },
    {
      id: "next-class",
      label: "Next Class",
      prompt: "What's my next class today and what should I prepare?",
      category: "schedule"
    },
    {
      id: "change-time",
      label: "Change Schedule",
      prompt: "Soon you'll be able to update lecture times with a single voice or text message! Feature in development.",
      isComingSoon: true,
      category: "schedule"
    },
    
  ];

// Static current lesson data
const CURRENT_LESSON = {
  subject: "Calculus",
  topic: "Chain Rule",
  time: "10:30 AM - 11:45 AM",
  objectives: [
    "Understanding Chain Rule concept",
    "Applying to composite functions",
    "Solving real-world problems",
  ],
  materials: ["Practice worksheets", "Visual examples", "Online calculator"],
  nextSteps: [
    "Review homework submissions",
    "Prepare quiz for next class",
    "Update progress tracker",
  ],
};

// Sidebar navigation items


const TeacherAssistant = () => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hello! I'm your AI teaching assistant. How can I help you today?",
      timestamp: new Date(),
    },
  ]);
  const elevenLabs = useRef(new ElevenLabsService(ELEVEN_LABS_API_KEY));
  const [isLoading, setIsLoading] = useState(false);
  const [isInCall, setIsInCall] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");

  const recognitionRef = useRef(null);
  const chatRef = useRef(null);
  const timerRef = useRef(null);
  const textToSpeech = useCallback(
    async (text) => {
      if (!isInCall) return;

      try {
        console.log("Starting text-to-speech for:", text);

        // Stop any current audio
        if (currentAudio) {
          currentAudio.pause();
          currentAudio.srcObject = null;
        }

        // Temporarily stop recognition
        if (recognitionRef.current && isListening) {
          console.log("Pausing recognition for speech");
          recognitionRef.current.stop();
        }

        // Get audio stream
        const audioStream = await elevenLabs.current.streamText(
          text,
          VOICE_ID,
          {
            model_id: "eleven_monolingual_v1",
            voice_settings: {
              stability: 0.5,
              similarity_boost: 0.5,
              speaking_rate: 1,
              style: 0.5,
            },
          }
        );

        console.log("Audio stream received, starting playback");

        // Play the audio stream
        await elevenLabs.current.playAudioStream(
          audioStream,
          () => {
            console.log("Audio playback started");
          },
          () => {
            console.log("Audio playback ended");
            // Resume recognition after speech ends
            if (isInCall && recognitionRef.current && !isListening) {
              console.log("Resuming recognition after speech");
              try {
                recognitionRef.current.start();
              } catch (error) {
                console.error(
                  "Error restarting recognition after speech:",
                  error
                );
              }
            }
          },
          (error) => {
            console.error("Audio playback error:", error);
            // Resume recognition on error
            if (isInCall && recognitionRef.current && !isListening) {
              try {
                recognitionRef.current.start();
              } catch (error) {
                console.error(
                  "Error restarting recognition after error:",
                  error
                );
              }
            }
          }
        );
      } catch (error) {
        console.error("Text-to-speech error:", error);
        // Resume recognition if there was an error
        if (isInCall && recognitionRef.current && !isListening) {
          try {
            recognitionRef.current.start();
          } catch (error) {
            console.error("Error restarting recognition after error:", error);
          }
        }
      }
    },
    [isInCall, currentAudio, isListening]
  );
  const sendMessageToOpenAI = useCallback(
    async (userMessage) => {
      setIsLoading(true);
      try {
        console.log("Sending message to OpenAI:", userMessage);

        // Create schedule context string
        const scheduleContext = `Current week schedule (${SCHEDULE_DATA.currentWeek}):
${SCHEDULE_DATA.days.map(day => `
${day.date}:
${day.classes.map(cls => `- ${cls.subject} (${cls.time}): ${cls.topic} in ${cls.room}`).join('\n')}`).join('\n')}`;

        const allMessages = [
          {
            role: "system",
            content: `You are Cortana, a helpful teaching assistant that helps teachers with lesson planning, content creation, and student assessment. You have access to the teacher's current schedule and can help with schedule-related queries.

Current Schedule Context:
${scheduleContext}

When responding to schedule-related queries:
1. Always check the provided schedule data
2. Be specific about times and dates
3. Consider class durations when suggesting free time
4. Mention any homework or materials due
5. Provide relevant context about classes that might be affected

Your responses should be concise, practical, and always reference accurate schedule information.`
          },
          ...messages.map((m) => ({ role: m.role, content: m.content })),
          { role: "user", content: userMessage },
        ];

        const response = await openai.chat.completions.create({
          model: "gpt-4",
          messages: allMessages,
          // max_tokens: 250,
          temperature: 0.7,
          presence_penalty: 0.6,
          frequency_penalty: 0.5,
          top_p: 0.9,
        });

        const assistantMessage = {
          role: "assistant",
          content: response.choices[0].message.content,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, assistantMessage]);

        if (isInCall) {
          try {
            await textToSpeech(assistantMessage.content);
          } catch (error) {
            console.error("Error in text-to-speech:", error);
          }
        }
      } catch (error) {
        console.error("Error in sendMessageToOpenAI:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [messages, isInCall, textToSpeech]
  );
  const parseScheduleQuery = (query) => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const currentTime = today.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: 'numeric', 
      hour12: true 
    });

    return {
      date: today,
      dayOfWeek,
      currentTime,
      query
    };
  };
  const processVoiceInput = useCallback(
    async (text) => {
      if (!text.trim()) return;

      try {
        console.log("Processing voice input:", text);

        const userMessage = {
          role: "user",
          content: text,
          timestamp: new Date(),
        };

        // Update messages with user input first
        setMessages((prev) => [...prev, userMessage]);

        // Then send to OpenAI and get response
        await sendMessageToOpenAI(text);
      } catch (error) {
        console.error("Error processing voice input:", error);
      }
    },
    [sendMessageToOpenAI]
  );

  // Update the cleanup useEffect to include currentAudio in dependencies
  useEffect(() => {
    return () => {
      stopTimer();
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.srcObject = null;
      }
    };
  }, [currentAudio]); // Added currentAudio to dependencies

  const initializeSpeechRecognition = useCallback(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition && !recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "en-US";

      recognitionRef.current.onstart = () => {
        console.log("Speech recognition started");
        setIsListening(true);
      };

      recognitionRef.current.onend = () => {
        console.log("Speech recognition ended");
        setIsListening(false);
        if (isInCall) {
          try {
            recognitionRef.current.start();
          } catch (error) {
            console.error("Error restarting recognition:", error);
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };

      recognitionRef.current.onresult = async (event) => {
        let finalTranscript = "";
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
            console.log("Final transcript:", finalTranscript);
            await processVoiceInput(finalTranscript.trim());
          } else {
            interimTranscript += transcript;
          }
        }

        setTranscript(interimTranscript);
      };
    }
  }, [isInCall, processVoiceInput]);

  const startCall = async () => {
    try {
      await initializeSpeechRecognition();

      if (recognitionRef.current) {
        recognitionRef.current.start();
      }

      setIsInCall(true);
      startTimer();

      console.log("Call started successfully");
    } catch (error) {
      console.error("Error starting call:", error);
    }
  };

  const endCall = () => {
    setIsInCall(false);
    stopTimer();

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (error) {
        console.error("Error stopping recognition:", error);
      }
    }

    if (currentAudio) {
      currentAudio.pause();
      currentAudio.srcObject = null;
      setCurrentAudio(null);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = {
      role: "user",
      content: message,
      timestamp: new Date(),
    };

    // Check if it's a schedule-related query
    if (message.toLowerCase().includes('schedule') || 
        message.toLowerCase().includes('class') ||
        message.toLowerCase().includes('time') ||
        message.toLowerCase().includes('free') ||
        message.toLowerCase().includes('available')) {
      const scheduleContext = parseScheduleQuery(message);
      userMessage.content = `[Schedule Query] ${message}
Current time: ${scheduleContext.currentTime}`;
    }

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    await sendMessageToOpenAI(userMessage.content);
  };

  const handleTemplateClick = async (template) => {
    if (template.isComingSoon) {
      const userMessage = {
        role: "user",
        content: `Check ${template.label.toLowerCase()} feature`,
        timestamp: new Date(),
      };
  
      const assistantMessage = {
        role: "assistant",
        content: `📝 ${template.prompt}\n\nIn the meantime, feel free to ask me about creating lesson plans, worksheets, or assessments!`,
        timestamp: new Date(),
      };
  
      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      return;
    }
  
    setMessage(template.prompt);
    await handleSendMessage();
  };

  const startTimer = () => {
    setCallDuration(0);
    timerRef.current = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, []);

  // Then, we'll create a cleanup function for the audio
  const cleanupAudio = useCallback(() => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.srcObject = null;
    }
  }, [currentAudio]);

  // Update the final cleanup useEffect
  useEffect(() => {
    // Return cleanup function
    return () => {
      stopTimer();
      cleanupAudio();
    };
  }, [stopTimer, cleanupAudio]);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  useEffect(() => {
    initializeSpeechRecognition();
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          console.error("Error cleaning up recognition:", error);
        }
      }
    };
  }, [initializeSpeechRecognition]);

  useEffect(() => {
    if (chatRef.current) {
      const scrollContainer = chatRef.current.parentElement;
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  useEffect(() => {
    return () => {
      stopTimer();
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.srcObject = null;
      }
    };
  }, []);

  return (
    <div className="flex h-screen bg-gray-50">
      <DemoSidebar />
      <div className="flex flex-1 flex-col">
        {/* Header */}
        <div className="w-full border-b bg-white">
          <div className="flex items-center justify-between px-6 py-3">
            <h1 className="text-lg font-medium text-gray-900">Teaching Assistant</h1>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 cursor-pointer">
                <Globe className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-700">English</span>
              </div>
              <div className="flex items-center space-x-2 cursor-pointer">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <Users className="w-4 h-4 text-gray-600" />
                </div>
                <span className="text-sm text-gray-700">Admin</span>
              </div>
            </div>
          </div>
        </div>
  
        {/* Main Content Area */}
        <div className="flex flex-1 overflow-hidden">
          {/* Chat Section */}
          <div className="flex-1 flex flex-col h-full">
            {/* Chat Header */}
            <div className="bg-white p-4 border-b">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="bg-gray-100 h-12 w-12 rounded-full flex items-center justify-center overflow-hidden">
                    <img src={aiImage} alt="AI Assistant" className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <h1 className="font-semibold text-xl">Cortana</h1>
                    <p className="text-sm text-gray-500">Online</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    onClick={startCall}
                    disabled={isInCall}
                  >
                    <Phone className="h-5 w-5 text-gray-700" />
                  </button>
                </div>
              </div>
            </div>
  
            {/* Messages Area - Scrollable */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6" ref={chatRef}>
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    {msg.role === "assistant" && (
                      <div className="bg-gray-100 h-8 w-8 rounded-full flex items-center justify-center mr-3">
                        <span className="font-medium">AI</span>
                      </div>
                    )}
                    <div
                      className={`max-w-[70%] ${
                        msg.role === "user"
                          ? "bg-gray-900 text-white rounded-2xl rounded-tr-none"
                          : "bg-gray-100 text-gray-900 rounded-2xl rounded-tl-none"
                      } p-4`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      <p className="text-xs opacity-70 mt-1">
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex">
                    <div className="bg-gray-100 h-8 w-8 rounded-full flex items-center justify-center mr-3">
                      <span className="font-medium">AI</span>
                    </div>
                    <div className="bg-gray-100 p-4 rounded-2xl rounded-tl-none">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
  
            {/* Input Area - Fixed */}
            <div className="bg-white border-t p-4">
              {/* Quick Templates */}
              <div className="overflow-x-auto mb-4">
                <div className="flex gap-1.5 px-1">
                  {QUICK_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateClick(template)}
                      className={`shrink-0 px-3 py-1.5 text-xs rounded-full transition-colors ${
                        template.isComingSoon 
                          ? "bg-zinc-900/10 text-zinc-900 hover:bg-zinc-900/20 border border-zinc-200"
                          : template.category === "schedule"
                          ? "bg-blue-50 text-blue-900 hover:bg-blue-100 border border-blue-200"
                          : template.category === "tasks"
                          ? "bg-purple-50 text-purple-900 hover:bg-purple-100 border border-purple-200"
                          : template.category === "content"
                          ? "bg-green-50 text-green-900 hover:bg-green-100 border border-green-200"
                          : template.category === "assessment"
                          ? "bg-amber-50 text-amber-900 hover:bg-amber-100 border border-amber-200"
                          : "bg-gray-100 hover:bg-gray-200"
                      } `}
                    >
                      <span className="flex items-center whitespace-nowrap">
                        {template.label}
                        {template.isComingSoon && (
                          <span className="ml-1.5 text-[10px] bg-zinc-900 text-white px-1.5 py-0.5 rounded-full">
                            Soon
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
  
              {/* Message Input */}
              <div className="flex space-x-3">
                <div className="flex-1 flex items-center bg-gray-100 rounded-xl px-4 py-2">
                  <input
                    type="text"
                    placeholder="Message Assitant AI..."
                    className="flex-1 bg-transparent outline-none"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <label className="p-1 hover:bg-gray-200 rounded-full cursor-pointer">
                    <input type="file" className="hidden" accept="image/*" />
                    <Image className="h-5 w-5 text-gray-500" />
                  </label>
                </div>
                <button
                  className="bg-gray-900 p-3 rounded-xl hover:bg-gray-800 transition-colors"
                  onClick={handleSendMessage}
                  disabled={isLoading}
                >
                  <Send className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>
          </div>
  
          {/* Right Sidebar - Independently Scrollable */}
          <div className="w-[320px] overflow-y-auto border-l bg-white">
            <div className="p-3 space-y-4">
              {/* AI Tutor Profile */}
              
  
              {/* Current Lesson */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold text-lg mb-4">Current Lesson</h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <h4 className="font-medium text-lg">{CURRENT_LESSON.subject}</h4>
                    <p className="text-gray-600">{CURRENT_LESSON.topic}</p>
                    <div className="flex items-center text-gray-500 mt-2">
                      <Clock className="h-4 w-4 mr-2" />
                      <span className="text-sm">{CURRENT_LESSON.time}</span>
                    </div>
                  </div>
  
                  <div>
                    <h4 className="font-medium mb-2">Objectives</h4>
                    <ul className="space-y-2">
                      {CURRENT_LESSON.objectives.map((objective, index) => (
                        <li key={index} className="flex items-start space-x-2 text-gray-600">
                          <span>•</span>
                          <span className="text-sm">{objective}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
  
                  <div>
                    <h4 className="font-medium mb-2">Materials Needed</h4>
                    <ul className="space-y-2">
                      {CURRENT_LESSON.materials.map((material, index) => (
                        <li key={index} className="flex items-start space-x-2 text-gray-600">
                          <span>•</span>
                          <span className="text-sm">{material}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        {/* Call Overlay */}
        {isInCall && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-8 w-96">
              <div className="flex flex-col items-center text-center">
                <div className="bg-gray-100 h-20 w-20 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl font-medium">AI</span>
                </div>
                <h2 className="text-xl font-semibold mb-1">Math Tutor AI</h2>
                <p className="text-gray-500 mb-2">Call in progress</p>
                <p className="text-lg font-medium mb-2">{formatDuration(callDuration)}</p>
                {isListening && transcript && (
                  <p className="text-sm text-gray-600 mb-4 italic">"{transcript}"</p>
                )}
                <div className="flex space-x-4">
                  <div className={`p-3 rounded-full ${isListening ? "bg-green-500" : "bg-gray-300"}`}>
                    {isListening ? (
                      <Mic className="w-6 h-6 text-white" />
                    ) : (
                      <MicOff className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <button
                    className="bg-red-500 p-3 rounded-full hover:bg-red-600 transition-colors"
                    onClick={endCall}
                  >
                    <PhoneOff className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherAssistant;
